// Shared pagination types
export type { PaginatedResult } from './pagination';
export { emptyPage } from './pagination';

// Query key factories
export {
  recipientKeys,
  tagKeys,
  groupKeys,
  customFieldKeys,
  customFieldPresetKeys,
  importKeys,
  auditKeys,
  workspaceAuditKeys,
  workspaceEmailKeys,
  suppressionKeys,
  dataQualityKeys,
  settingsKeys,
  teamKeys,
  securityKeys,
  onboardingKeys,
  feedbackKeys,
  companyKeys,
  billingKeys,
  adminKeys,
  aiChatKeys,
  passkeyKeys,
  recycleBinKeys,
  profileKeys,
  stagingKeys,
  workspaceKeys,
  domainKeys,
  viewPreferenceKeys,
  homeDashboardKeys,
} from './query-keys';

// View preferences (Redis-backed per-user list-view state)
export { useViewPreferences } from '@/hooks/use-view-preferences';
export type {
  ViewPreferenceState,
  RecipientsViewPreferenceState,
  ViewPreferenceGetResponse,
  ViewPreferencePutResponse,
} from '@/lib/types/view-preferences';

// Recipient hooks
export {
  useRecipients,
  useRecipient,
  useRecipientMetrics,
  useRecipientFilterOptions,
  useRecipientCount,
  useRecipientDuplicates,
  useRecipientGroups,
  useRecipientTags,
  useRecipientHistory,
  useRecipientWorkflowActivity,
  useRecipientOwnActivity,
  useCreateRecipient,
  useUpdateRecipient,
  useUpdateRecipientTags,
  useDeleteRecipient,
  useBulkDeleteRecipients,
  useBulkUpdateStatus,
  useBulkUpdateField,
  useBulkCreateRecipients,
  useMergeRecipients,
  useReplaceRecipientTags,
  useUpdateCustomFields,
  useBulkAssignGroups,
  useBulkRemoveGroups,
} from '@/components/recipients/hooks/use-recipients';
export type { RecipientFilter, RecipientWorkflowActivity, RecipientOwnActivity } from '@/components/recipients/hooks/use-recipients';

// Tag hooks
export {
  useTags,
  useTagsPaginated,
  useCreateTag,
  useUpdateTag,
  useDeleteTag,
  useBulkDeleteTags,
  useMergeTags,
  useAssignTagToRecipients,
  useRemoveTagFromRecipients,
  useBulkAssignTagsToRecipients,
  useBulkRemoveTagsFromRecipients,
  useResolveTagIds,
} from '@/components/recipients/hooks/use-tags';
export type { Tag, ResolvedTagIds } from '@/components/recipients/hooks/use-tags';

// Group hooks
export {
  useGroups,
  useGroupsPaginated,
  useGroup,
  useGroupMembers,
  useGroupMemberIds,
  useGroupMembersList,
  useGroupMetrics,
  useCreateGroup,
  useUpdateGroup,
  useDeleteGroup,
  useBulkDeleteGroups,
  useAddGroupMembers,
  useRemoveGroupMembers,
  useDuplicateGroup,
  useMergeGroups,
  useRefreshGroup,
} from '@/components/recipients/hooks/use-groups';

// Custom field preset hooks
export {
  useRecipientCustomFieldPresets,
} from '@/components/recipients/hooks/use-recipient-custom-field-presets';
export type { RecipientCustomFieldPreset, ListPresetsResponse } from '@/components/recipients/hooks/use-recipient-custom-field-presets';

// Custom field hooks
export {
  useCustomFields,
  useCustomFieldsPaginated,
  useCustomFieldMetrics,
  useCreateCustomField,
  useUpdateCustomField,
  useDeleteCustomField,
  useBulkCustomFieldAction,
  useArchiveCustomField,
  useRestoreCustomField,
  useReorderCustomFields,
} from '@/components/recipients/hooks/use-custom-fields';

// Import hooks
export {
  useImportHistory,
  useImportHistoryPaginated,
  useImportStatus,
  useAnalyzeImport,
  useProcessImport,
  useRollbackImport,
} from '@/components/recipients/hooks/use-import';

// Audit hooks
export { useAuditLogs, useAuditMetrics } from '@/components/recipients/hooks/use-audit';
export type { AuditLog } from '@/components/recipients/hooks/use-audit';

// Suppression hooks
export {
  useSuppressionList,
  useSuppressionMetrics,
  useAddSuppression,
  useRemoveSuppression,
  useUpdateSuppression,
  useBulkSuppression,
  useBulkDeleteSuppression,
} from '@/components/recipients/hooks/use-suppression';
export type { SuppressionEntry } from '@/components/recipients/hooks/use-suppression';

// Recycle Bin hooks
export {
  useRecycleBin,
  useRestoreRecipient,
  usePermanentDelete,
  useBulkRestoreRecipients,
  useBulkPermanentDelete,
  collectRecycleBinIds,
  usePurgeRecycleBin,
  useRecycleBinPurgeCount,
  emptyRecycleBinPage,
} from '@/components/recipients/hooks/use-recycle-bin';
export type {
  DeletedRecipient,
  RecycleBinResponse,
  RecycleBinPageResult,
  PurgeRecycleBinResult,
} from '@/components/recipients/hooks/use-recycle-bin';

// Data quality hooks
export {
  useDataQualityMetrics,
  useIncompleteProfiles,
  useFieldCoverage,
} from '@/components/recipients/hooks/use-data-quality';
export type { DataQualityMetrics, FieldCoverage } from '@/components/recipients/hooks/use-data-quality';

// Settings hooks
export {
  useRecipientSettings,
  useUpdateRecipientSettings,
  useResetRecipientSettings,
} from '@/components/recipients/hooks/use-settings';
export type { RecipientSettings, FieldSource, UpdateRecipientSettingsDto } from '@/components/recipients/hooks/use-settings';

// Onboarding hooks
export {
  useOnboardingStatus,
  useOnboardingChecklist,
  useCreateCompany,
  useOAuthBootstrap,
  useCompleteStep,
  useUpdateOnboardingContext,
  useCompleteAllSteps,
  useInviteTeamMember as useOnboardingInvite,
  useOnboardingPrefill,
} from '@/components/settings/hooks/use-onboarding';
export type {
  OnboardingStatus,
  CreateCompanyDto,
  CompanyCreatedResponse,
  CompleteStepDto,
  CompleteStepResponse,
  ChecklistItem,
  ChecklistResponse,
  OAuthBootstrapDto,
  OAuthBootstrapResponse,
} from '@/components/settings/hooks/use-onboarding';

// Smart group hooks were deleted 2026-05-25 — smart groups and dynamic groups
// are the same concept (recipient_groups.type='Dynamic'). Use useGroups()
// from '@/components/recipients/hooks/use-groups' and filter to type='Dynamic'.
// See docs/superpowers/specs/2026-05-25-recipients-groups-unification-design.md.

// Integration hooks
export {
  integrationKeys,
  useIntegration,
  useConnectIntegration,
  useDisconnectIntegration,
  useSyncIntegration,
  useUpdateIntegrationSchedule,
  useUpdateIntegrationSyncConfig,
} from '@/components/settings/hooks/use-integrations';

// Org chart hooks
export {
  orgChartKeys,
  useOrgChart,
  useOrgChartSubtree,
  useOrgChartSearch,
} from '@/components/recipients/hooks/use-org-chart';

// Team hooks
export {
  useTeamMembers,
  useInviteTeamMember,
  useUpdateTeamMemberRole,
  useDeactivateTeamMember,
} from '@/components/settings/hooks/use-team';
export type { TeamMember } from '@/components/settings/hooks/use-team';

// Security hooks
export {
  useSessions,
  useSecuritySettings,
  useUpdateSecuritySettings,
  useRevokeSession,
  useRevokeAllOtherSessions,
  useChangePassword,
} from '@/components/settings/hooks/use-security';
export type { Session, SecuritySettings } from '@/components/settings/hooks/use-security';

// Workspace hooks
export {
  useWorkspaces,
} from '@/components/tenant/hooks/use-workspaces';
export type { Workspace } from '@/components/tenant/hooks/use-workspaces';

// Domain hooks
export {
  useDomains,
} from '@/components/tenant/hooks/use-domains';
export type { Domain } from '@/components/tenant/hooks/use-domains';

// Company report hooks
export {
  companyReportKeys,
  useCompanyOverview,
  useCompanyRecipients,
  useCompanyCampaigns,
  useCompanyRisk,
  useCompanyActivity,
} from '@/components/settings/hooks/use-company-reports';
export type {
  CompanyOverview,
  WorkspaceRecipients,
  DomainRecipients,
  CompanyRecipientsReport,
  Campaign,
  CompanyCampaignsReport,
  RiskLevel,
  CompanyRiskReport,
  Activity,
  CompanyActivityReport,
} from '@/components/settings/hooks/use-company-reports';

// Feedback hooks
export {
  useFeedbackTickets,
  useFeedbackTicket,
  useFeedbackStats,
  useFeedbackThread,
  useCreateFeedback,
  useUpdateFeedback,
  useDeleteFeedback,
  useSendThreadMessage,
  useBulkFeedbackAction,
} from '@/components/settings/hooks/use-feedback';

// AI Chat hooks
export {
  useAiConversations,
  useAiConversation,
  useAiChatSettings,
  useCreateAiConversation,
  useUpdateAiConversation,
  useDeleteAiConversation,
  useUpdateAiChatSettings,
} from '@/components/settings/hooks/use-ai-chat';
export type {
  AiConversation,
  AiMessage,
  AiConversationWithMessages,
  AiChatSettings,
} from '@/components/settings/hooks/use-ai-chat';

// Passkey hooks
export {
  usePasskeys,
  usePasskeyStatus,
  useDeletePasskey,
} from '@/components/auth/hooks/use-passkeys';
export type { PasskeyStatus } from '@/components/auth/hooks/use-passkeys';

// Profile hooks
export {
  useProfile,
  useUpdateProfile,
} from '@/components/settings/hooks/use-profile';
export type { UserProfile, UpdateProfileDto } from '@/components/settings/hooks/use-profile';

// Company hooks
export {
  useCompanyProfile,
  useCompanySettings,
  useUpdateCompanyProfile,
  useUpdateCompanySettings,
} from '@/components/settings/hooks/use-company';
export type {
  CompanyProfile,
  UpdateCompanyProfileDto,
  CompanySettings,
  UpdateCompanySettingsDto,
} from '@/components/settings/hooks/use-company';

// Billing hooks
export {
  useSubscription,
  useUsage,
} from '@/components/settings/hooks/use-billing';
export type { Subscription, Usage } from '@/components/settings/hooks/use-billing';

// Staging hooks
export {
  usePendingCount,
  useStagingList,
  useResolveConflict,
  useBulkResolve,
} from '@/components/recipients/hooks/use-staging';
export type { StagingFilters } from '@/components/recipients/hooks/use-staging';

// Provider groups hook (integration wizard)
export { useProviderGroups } from '@/components/settings/hooks/use-provider-groups'

// Provider fields hook (field mapping wizard)
export { useProviderFields } from '@/components/settings/hooks/use-provider-fields'
export type { ProviderField, AvailableFieldsResponse } from '@/components/settings/hooks/use-provider-fields'

// Admin (Super Admin) hooks
export {
  useAdminLogin,
  useAdminMe,
  useAdminDashboard,
  useAdminCompanies,
  useCreateAdminCompany,
  useUpdateAdminCompany,
  useToggleCompanyStatus,
  useAdminWorkspaces,
  useCreateAdminWorkspace,
  useAdminUsers,
  useAdminSuperAdmins,
  useCreateSuperAdmin,
  useAdminAuditLogs,
  useAdminFeedback,
  useAdminFeedbackStats,
  useImpersonate,
} from '@/components/settings/hooks/use-admin';

// Home Dashboard hook
export { useHomeDashboard } from './use-home-dashboard';
export type {
  DashboardHomeData,
  RiskIndex,
  AttentionItem,
  KpiTile,
  DepartmentCompletion,
  ErRemediationWeek,
  UpcomingItem,
  ModuleTile,
  TrendPoint,
} from './use-home-dashboard';
